// src/contexts/TimezoneContext.tsx
// Step 9: Frontend Timezone Integration - Timezone Context

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { format, parseISO, formatDistanceToNow, isValid } from 'date-fns';
import { toZonedTime, fromZonedTime, format as formatTz } from 'date-fns-tz';
import { setApiTimezone } from '../services/api';

export interface TimezoneInfo {
  timezone: string;
  offset: number;
  abbreviation: string;
  isDST: boolean;
  displayName: string;
}

export interface TimezonePreferences {
  preferredTimezone: string;
  autoDetectTimezone: boolean;
  show24HourFormat: boolean;
  showTimezoneInDates: boolean;
  useLocalTimezone: boolean;
}

export interface TimezoneContextType {
  // Current timezone state
  timezone: string;
  timezoneLabel: string;
  setTimezone: (timezone: string) => void;
  
  // User preferences
  autoDetectTimezone: boolean;
  setAutoDetectTimezone: (autoDetect: boolean) => void;
  use24HourFormat: boolean;
  setUse24HourFormat: (use24Hour: boolean) => void;
  showTimezoneAbbreviation: boolean;
  setShowTimezoneAbbreviation: (show: boolean) => void;
  convertToLocalTimezone: boolean;
  setConvertToLocalTimezone: (convert: boolean) => void;
  
  // Timezone utilities
  formatDate: (date: string | Date, formatStr?: string) => string;
  formatTime: (date: string | Date, formatStr?: string) => string;
  formatDateTime: (date: string | Date, formatStr?: string) => string;
  formatRelativeTime: (date: string | Date) => string;
  formatDateRange: (startDate: string | Date, endDate: string | Date) => string;
  
  // Timezone conversion
  toUserTimezone: (date: string | Date) => Date;
  toUTC: (date: string | Date) => Date;
  convertTimezone: (date: string | Date, fromTz: string, toTz: string) => Date;
  

  getTimezoneOffset: (timezone?: string) => number;
  isDST: (date: string | Date, timezone?: string) => boolean;
  
  // Event system
  onTimezoneChange: (callback: (timezone: string) => void) => () => void;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

interface TimezoneProviderProps {
  children: ReactNode;
}

// Supported timezones with metadata
export const SUPPORTED_TIMEZONES = [
  // US Timezones
  { 
    value: 'America/New_York', 
    label: 'Eastern Time (ET)', 
    country: 'US', 
    offset: -5,
    dstOffset: -4
  },
  { 
    value: 'America/Chicago', 
    label: 'Central Time (CT)', 
    country: 'US', 
    offset: -6,
    dstOffset: -5
  },
  { 
    value: 'America/Denver', 
    label: 'Mountain Time (MT)', 
    country: 'US', 
    offset: -7,
    dstOffset: -6
  },
  { 
    value: 'America/Los_Angeles', 
    label: 'Pacific Time (PT)', 
    country: 'US', 
    offset: -8,
    dstOffset: -7
  },
  { 
    value: 'America/Anchorage', 
    label: 'Alaska Time (AKT)', 
    country: 'US', 
    offset: -9,
    dstOffset: -8
  },
  { 
    value: 'Pacific/Honolulu', 
    label: 'Hawaii Time (HST)', 
    country: 'US', 
    offset: -10,
    dstOffset: -10
  },
  // Canadian Timezones
  { 
    value: 'America/Toronto', 
    label: 'Eastern Time (Canada)', 
    country: 'CA', 
    offset: -5,
    dstOffset: -4
  },
  { 
    value: 'America/Winnipeg', 
    label: 'Central Time (Canada)', 
    country: 'CA', 
    offset: -6,
    dstOffset: -5
  },
  { 
    value: 'America/Edmonton', 
    label: 'Mountain Time (Canada)', 
    country: 'CA', 
    offset: -7,
    dstOffset: -6
  },
  { 
    value: 'America/Vancouver', 
    label: 'Pacific Time (Canada)', 
    country: 'CA', 
    offset: -8,
    dstOffset: -7
  },
  // International Timezones
  { 
    value: 'UTC', 
    label: 'UTC', 
    country: 'International', 
    offset: 0,
    dstOffset: 0
  },
  { 
    value: 'Europe/London', 
    label: 'London (GMT/BST)', 
    country: 'UK', 
    offset: 0,
    dstOffset: 1
  },
  { 
    value: 'Europe/Paris', 
    label: 'Paris (CET/CEST)', 
    country: 'FR', 
    offset: 1,
    dstOffset: 2
  },
  { 
    value: 'Asia/Tokyo', 
    label: 'Tokyo (JST)', 
    country: 'JP', 
    offset: 9,
    dstOffset: 9
  },
  { 
    value: 'Australia/Sydney', 
    label: 'Sydney (AEST/AEDT)', 
    country: 'AU', 
    offset: 10,
    dstOffset: 11
  },
];



export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  
  // State management
  const [timezone, setTimezoneState] = useState<string>('America/New_York');
  const [autoDetectTimezone, setAutoDetectTimezone] = useState<boolean>(true);
  const [use24HourFormat, setUse24HourFormat] = useState<boolean>(false);
  const [showTimezoneAbbreviation, setShowTimezoneAbbreviation] = useState<boolean>(true);
  const [convertToLocalTimezone, setConvertToLocalTimezone] = useState<boolean>(true);
  
  // Event listeners
  const [timezoneChangeListeners, setTimezoneChangeListeners] = useState<((timezone: string) => void)[]>([]);

  // Auto-detect browser timezone with intelligent fallback
  const detectBrowserTimezone = useCallback((): string => {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Check if browser timezone is in our supported list
      const supportedTimezone = SUPPORTED_TIMEZONES.find(tz => tz.value === browserTimezone);
      if (supportedTimezone) {
        return browserTimezone;
      }
      
      // Fallback mapping for common timezones
      const timezoneMapping: Record<string, string> = {
        'America/New_York': 'America/New_York',
        'America/Detroit': 'America/New_York',
        'America/Indianapolis': 'America/New_York',
        'America/Chicago': 'America/Chicago',
        'America/Denver': 'America/Denver',
        'America/Phoenix': 'America/Denver',
        'America/Los_Angeles': 'America/Los_Angeles',
        'America/Toronto': 'America/Toronto',
        'Europe/London': 'Europe/London',
        'Europe/Paris': 'Europe/Paris',
        'Asia/Tokyo': 'Asia/Tokyo',
        'Australia/Sydney': 'Australia/Sydney',
  };

      // Try to find a close match
      for (const [pattern, fallback] of Object.entries(timezoneMapping)) {
        if (browserTimezone.includes(pattern.split('/')[1])) {
          return fallback;
        }
      }
      
      // Default fallback
      return 'America/New_York';
    } catch (error) {
      console.warn('Failed to detect browser timezone:', error);
      return 'America/New_York';
    }
  }, []);

  // Initialize timezone from localStorage or auto-detect
  useEffect(() => {
    try {
      const savedTimezone = localStorage.getItem('user-timezone');
      const savedAutoDetect = localStorage.getItem('auto-detect-timezone') === 'true';
      const saved24Hour = localStorage.getItem('use-24-hour-format') === 'true';
      const savedShowAbbr = localStorage.getItem('show-timezone-abbreviation') !== 'false';
      const savedConvertLocal = localStorage.getItem('convert-to-local-timezone') !== 'false';
      
      setAutoDetectTimezone(savedAutoDetect);
      setUse24HourFormat(saved24Hour);
      setShowTimezoneAbbreviation(savedShowAbbr);
      setConvertToLocalTimezone(savedConvertLocal);
      
      if (savedAutoDetect) {
        const detectedTimezone = detectBrowserTimezone();
        setTimezoneState(detectedTimezone);
      } else if (savedTimezone && SUPPORTED_TIMEZONES.find(tz => tz.value === savedTimezone)) {
        setTimezoneState(savedTimezone);
      } else {
        const detectedTimezone = detectBrowserTimezone();
        setTimezoneState(detectedTimezone);
      }
    } catch (error) {
      console.warn('Failed to load timezone preferences:', error);
      const detectedTimezone = detectBrowserTimezone();
      setTimezoneState(detectedTimezone);
    }
  }, [detectBrowserTimezone]);

  // Save preferences to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('user-timezone', timezone);
      localStorage.setItem('auto-detect-timezone', autoDetectTimezone.toString());
      localStorage.setItem('use-24-hour-format', use24HourFormat.toString());
      localStorage.setItem('show-timezone-abbreviation', showTimezoneAbbreviation.toString());
      localStorage.setItem('convert-to-local-timezone', convertToLocalTimezone.toString());
    } catch (error) {
      console.warn('Failed to save timezone preferences:', error);
    }
  }, [timezone, autoDetectTimezone, use24HourFormat, showTimezoneAbbreviation, convertToLocalTimezone]);

  // Push the active timezone into the axios instance so every API request
  // carries an X-Timezone header. The api module falls back to the live
  // browser zone when this is null (e.g. before the provider has mounted).
  useEffect(() => {
    setApiTimezone(timezone);
    return () => setApiTimezone(null);
  }, [timezone]);

  // Auto-detect timezone when setting changes
  useEffect(() => {
    if (autoDetectTimezone) {
      const detectedTimezone = detectBrowserTimezone();
      if (detectedTimezone !== timezone) {
        setTimezoneState(detectedTimezone);
      }
    }
  }, [autoDetectTimezone, detectBrowserTimezone, timezone]);

  // Handle timezone changes
  const setTimezone = useCallback((newTimezone: string) => {
    if (SUPPORTED_TIMEZONES.find(tz => tz.value === newTimezone)) {
      setTimezoneState(newTimezone);
      setAutoDetectTimezone(false);
      
      // Notify listeners
      timezoneChangeListeners.forEach(listener => listener(newTimezone));
    }
  }, [timezoneChangeListeners]);

  // Get timezone label
  const timezoneLabel = SUPPORTED_TIMEZONES.find(tz => tz.value === timezone)?.label || timezone;

  // Date formatting utilities
  const formatDate = useCallback((date: string | Date, formatStr: string = 'MMM dd, yyyy'): string => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return 'Invalid Date';
      
      if (convertToLocalTimezone) {
        const zonedDate = toZonedTime(dateObj, timezone);
        return formatTz(zonedDate, formatStr, { timeZone: timezone });
      }
      
      return format(dateObj, formatStr);
    } catch (error) {
      console.warn('Date formatting error:', error);
      return 'Invalid Date';
    }
  }, [timezone, convertToLocalTimezone]);

  const formatTime = useCallback((date: string | Date, formatStr?: string): string => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return 'Invalid Time';
      
      const defaultFormat = use24HourFormat ? 'HH:mm' : 'h:mm a';
      const timeFormat = formatStr || defaultFormat;
      
      if (convertToLocalTimezone) {
        const zonedDate = toZonedTime(dateObj, timezone);
        let formatted = formatTz(zonedDate, timeFormat, { timeZone: timezone });
      
        if (showTimezoneAbbreviation && !formatStr) {
          const abbr = formatTz(zonedDate, 'zzz', { timeZone: timezone });
          formatted += ` ${abbr}`;
      }
      
      return formatted;
      }
      
      return format(dateObj, timeFormat);
    } catch (error) {
      console.warn('Time formatting error:', error);
      return 'Invalid Time';
    }
  }, [timezone, convertToLocalTimezone, use24HourFormat, showTimezoneAbbreviation]);

  const formatDateTime = useCallback((date: string | Date, formatStr?: string): string => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return 'Invalid DateTime';
      
      const defaultFormat = use24HourFormat ? 'MMM dd, yyyy HH:mm' : 'MMM dd, yyyy h:mm a';
      const dateTimeFormat = formatStr || defaultFormat;
      
      if (convertToLocalTimezone) {
        const zonedDate = toZonedTime(dateObj, timezone);
        let formatted = formatTz(zonedDate, dateTimeFormat, { timeZone: timezone });
      
        if (showTimezoneAbbreviation && !formatStr) {
          const abbr = formatTz(zonedDate, 'zzz', { timeZone: timezone });
          formatted += ` ${abbr}`;
        }
        
        return formatted;
      }
      
      return format(dateObj, dateTimeFormat);
    } catch (error) {
      console.warn('DateTime formatting error:', error);
      return 'Invalid DateTime';
    }
  }, [timezone, convertToLocalTimezone, use24HourFormat, showTimezoneAbbreviation]);

  const formatRelativeTime = useCallback((date: string | Date): string => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return 'Invalid Date';
      
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch (error) {
      console.warn('Relative time formatting error:', error);
      return 'Invalid Date';
    }
  }, []);
      
  const formatDateRange = useCallback((startDate: string | Date, endDate: string | Date): string => {
    try {
      const start = formatDate(startDate);
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    } catch (error) {
      console.warn('Date range formatting error:', error);
      return 'Invalid Date Range';
    }
  }, [formatDate]);

  // Timezone conversion utilities
  const toUserTimezone = useCallback((date: string | Date): Date => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) throw new Error('Invalid date');
      
      return toZonedTime(dateObj, timezone);
    } catch (error) {
      console.warn('Timezone conversion error:', error);
      return new Date();
    }
  }, [timezone]);

  const toUTC = useCallback((date: string | Date): Date => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) throw new Error('Invalid date');
      
      return fromZonedTime(dateObj, timezone);
    } catch (error) {
      console.warn('UTC conversion error:', error);
      return new Date();
    }
  }, [timezone]);

  const convertTimezone = useCallback((date: string | Date, fromTz: string, toTz: string): Date => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) throw new Error('Invalid date');
      
      // Convert from source timezone to UTC, then to target timezone
      const utcDate = fromZonedTime(dateObj, fromTz);
      return toZonedTime(utcDate, toTz);
    } catch (error) {
      console.warn('Timezone conversion error:', error);
      return new Date();
    }
  }, []);



  const getTimezoneOffset = useCallback((tz?: string): number => {
    try {
      const targetTimezone = tz || timezone;
      const timezoneInfo = SUPPORTED_TIMEZONES.find(t => t.value === targetTimezone);
      
      if (timezoneInfo) {
        // Check if DST is currently active
        const now = new Date();
        const isDSTActive = isDST(now, targetTimezone);
        return isDSTActive ? timezoneInfo.dstOffset : timezoneInfo.offset;
      }
      
      // Fallback to JavaScript's timezone offset
      const now = new Date();
      const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzDate = new Date(now.toLocaleString('en-US', { timeZone: targetTimezone }));
      return (utcDate.getTime() - tzDate.getTime()) / (1000 * 60 * 60);
    } catch (error) {
      console.warn('Timezone offset calculation error:', error);
      return 0;
    }
  }, [timezone]);

  const isDST = useCallback((date: string | Date, tz?: string): boolean => {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return false;
      
      const targetTimezone = tz || timezone;
      
      // Create two dates: one in January (definitely not DST) and one at the target date
      const jan = new Date(dateObj.getFullYear(), 0, 1);
      const janOffset = new Date(jan.toLocaleString('en-US', { timeZone: 'UTC' })).getTime() - 
                      new Date(jan.toLocaleString('en-US', { timeZone: targetTimezone })).getTime();
      
      const targetOffset = new Date(dateObj.toLocaleString('en-US', { timeZone: 'UTC' })).getTime() - 
                          new Date(dateObj.toLocaleString('en-US', { timeZone: targetTimezone })).getTime();
      
      return targetOffset !== janOffset;
    } catch (error) {
      console.warn('DST check error:', error);
      return false;
    }
  }, [timezone]);

  // Event system
  const onTimezoneChange = useCallback((callback: (timezone: string) => void) => {
    setTimezoneChangeListeners(prev => [...prev, callback]);
    
    // Return cleanup function
    return () => {
      setTimezoneChangeListeners(prev => prev.filter(listener => listener !== callback));
    };
  }, []);

  const contextValue: TimezoneContextType = {
    // Current timezone state
    timezone,
    timezoneLabel,
    setTimezone,
    
    // User preferences
    autoDetectTimezone,
    setAutoDetectTimezone,
    use24HourFormat,
    setUse24HourFormat,
    showTimezoneAbbreviation,
    setShowTimezoneAbbreviation,
    convertToLocalTimezone,
    setConvertToLocalTimezone,
    
    // Timezone utilities
    formatDate,
    formatTime,
    formatDateTime,
    formatRelativeTime,
    formatDateRange,
    
    // Timezone conversion
    toUserTimezone,
    toUTC,
    convertTimezone,
    

    getTimezoneOffset,
    isDST,
    
    // Event system
    onTimezoneChange,
  };

  return (
    <TimezoneContext.Provider value={contextValue}>
      {children}
    </TimezoneContext.Provider>
  );
}; 